import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import Header from '../../components/Header'
import { useTheme } from '../../contexts/ThemeContext'
import { leaderboardAPI, getApiBaseUrlWithoutApi } from '../../config/api'
import UserProfileModal from '../../components/UserProfileModal'
import 'primeicons/primeicons.css'

export default function LeaderboardPage() {
  const { theme } = useTheme()
  const [searchQuery, setSearchQuery] = useState('')
  const [players, setPlayers] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 20

  const fetchPlayers = async () => {
    try {
      setLoading(true)
      const data = await leaderboardAPI.getTopPlayers()
      
      // Xử lý avatar URL để đảm bảo hiển thị đúng
      const API_BASE_URL = getApiBaseUrlWithoutApi()
      const processedData = data.map((player: any) => {
        if (player.avatar) {
          if (player.avatar.startsWith('/media/')) {
            player.avatar = `${API_BASE_URL}${player.avatar}`
          } else if (!player.avatar.startsWith('http') && !player.avatar.startsWith('/images/')) {
            player.avatar = `${API_BASE_URL}${player.avatar}`
          }
        }
        return player
      })
      
      setPlayers(processedData)
    } catch (error) {
      setPlayers([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPlayers()
    
    // Auto-refresh mỗi 30 giây để cập nhật bảng xếp hạng
    const interval = setInterval(() => {
      fetchPlayers()
    }, 30000) // 30 giây
    
    // Refresh khi user quay lại tab/window
    const handleFocus = () => {
      fetchPlayers()
    }
    window.addEventListener('focus', handleFocus)
    
    return () => {
      clearInterval(interval)
      window.removeEventListener('focus', handleFocus)
    }
  }, [])

  const handleAvatarClick = (userId: number) => {
    setSelectedUserId(userId)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedUserId(null)
  }

  const top3 = players.slice(0, 3)

  // Filter players based on search
  const filteredPlayers = players.filter(player =>
    (player.name || '').toLowerCase().includes(searchQuery.toLowerCase())
  )

  // Pagination logic
  const totalPages = Math.ceil(filteredPlayers.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedPlayers = filteredPlayers.slice(startIndex, endIndex)

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery])

  // Kiểm tra xem user có phải là user mới không (đăng ký trong 3 ngày gần đây)
  const isNewUser = (dateJoined: string | null | undefined): boolean => {
    if (!dateJoined) return false
    const joinedDate = new Date(dateJoined)
    const now = new Date()
    const diffTime = Math.abs(now.getTime() - joinedDate.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays <= 3
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'up':
        return <i className="pi pi-arrow-up text-green-400"></i>
      case 'down':
        return <i className="pi pi-arrow-down text-red-400"></i>
      case 'new':
        return <span className="px-1 py-0.5 bg-blue-500 text-white text-[8px] md:text-[9px] font-bold rounded">MỚI</span>
      default:
        return <i className="pi pi-user text-gray-400"></i>
    }
  }

  const getPlayerIcon = (_status: string, rank: number) => {
    if (rank === 1) return <i className="pi pi-trophy text-gold-400 text-xl md:text-2xl"></i>
    if (rank === 2) return <span className="text-xl md:text-2xl block leading-none">🥈</span>
    if (rank === 3) return <span className="text-xl md:text-2xl block leading-none">🥉</span>
    return <span className="text-sm md:text-base font-bold text-gray-600">{rank}</span>
  }

  return (
    <main className={`min-h-screen relative ${theme === 'dark' ? 'bg-black' : 'bg-gray-200'}`}>
      {/* Fixed Background Image - covers entire page from header to footer, doesn't scroll */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <img
          src="/images/leaderboard-bg.jpg"
          alt="Leaderboard background"
          className="w-full h-full object-cover"
          style={{ opacity: 0.8 }}
        />
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/70"></div>
      </div>

      {/* Content wrapper with z-index to appear above background */}
      <div className="relative z-10">
        <Header />

        {/* Top Section - Shorter */}
        <section className="relative py-8 md:py-12 overflow-hidden">
          <div className="container mx-auto relative z-10 px-4">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
            {/* Title Section */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="flex-1"
            >
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-1 text-white">
                Bảng Xếp Hạng
              </h1>
              <p className="text-sm md:text-base font-semibold text-gold-400">
                Tháng {new Date().getMonth() + 1}/{new Date().getFullYear()}
              </p>
            </motion.div>

            {/* Search Box - Right Side */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative w-full md:w-auto"
            >
              <div className="relative">
                <input
                  type="text"
                  placeholder="Tìm kiếm cầu thủ..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`pl-10 pr-4 py-2.5 border-2 rounded-lg focus:outline-none focus:ring-2 transition w-full md:w-64 shadow-lg ${
                    theme === 'dark' 
                      ? 'bg-black/90 border-gold-500 text-white placeholder-gray-400 focus:border-gold-400 focus:ring-gold-500/50' 
                      : 'bg-white/90 border-gold-400 text-gray-900 placeholder-gray-500 focus:border-gold-500 focus:ring-gold-400/50'
                  }`}
                />
                <i className="pi pi-search absolute left-3 top-1/2 -translate-y-1/2 text-gold-400 text-lg"></i>
              </div>
            </motion.div>
          </div>

          {/* Top 3 Podium - Closer Together */}
          <div className="mb-4 md:mb-6 flex items-end justify-center gap-2 md:gap-3 px-2">
            {/* Rank 2 - Silver */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="flex flex-col items-center cursor-pointer group w-1/3 max-w-[120px] md:max-w-[150px]"
            >
              <div className="mb-2 rounded-full p-2.5 bg-gray-300 transition-transform group-hover:scale-110 flex items-center justify-center">
                <span className="text-xl md:text-2xl block leading-none">🥈</span>
              </div>
              <div className="relative mb-3">
                <span 
                  className="relative flex shrink-0 overflow-hidden rounded-full h-14 w-14 md:h-20 md:w-20 border-4 border-gray-400 transition-transform group-hover:scale-105 cursor-pointer"
                  onClick={() => top3[1]?.id && handleAvatarClick(top3[1].id)}
                >
                  {top3[1]?.avatar ? (
                    <img
                      src={top3[1].avatar}
                      alt={top3[1]?.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                      <span className="text-gray-600 font-bold text-lg">
                        {(top3[1]?.name || 'U').charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                </span>
                <div className="absolute -bottom-1 -right-1 flex items-center gap-1 rounded-full bg-gold-500 px-2.5 py-1 text-xs font-bold text-black">
                  <span style={{ color: '#dfae30' }}>🔥</span>
                  <span>{top3[1]?.streak}</span>
                </div>
              </div>
              <h3 className="text-xs md:text-sm font-bold text-center break-words px-1 mb-1 text-white leading-tight">
                {top3[1]?.name || 'N/A'}
              </h3>
              <div className="text-2xl md:text-3xl font-bold text-white">{top3[1]?.total_points || top3[1]?.points || 0}</div>
              <div className="text-[8px] md:text-[9px] uppercase tracking-wider mb-2 text-gray-300">Điểm</div>
              <div className="mt-2 w-full rounded-t-lg transition-transform group-hover:scale-105 h-20 md:h-24 bg-gray-300 flex items-center justify-center">
                <div className="text-3xl md:text-4xl font-bold text-gray-600/50">2</div>
              </div>
            </motion.div>

            {/* Rank 1 - Gold */}
            <motion.div
              initial={{ opacity: 0, y: 50, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex flex-col items-center cursor-pointer group w-1/3 max-w-[130px] md:max-w-[170px] -mb-2"
            >
              <div className="mb-2 rounded-full p-2.5 bg-gold-400 transition-transform group-hover:scale-110 flex items-center justify-center">
                <i className="pi pi-trophy text-gold-700 text-xl md:text-2xl"></i>
              </div>
              <div className="relative mb-3">
                <span 
                  className="relative flex shrink-0 overflow-hidden rounded-full h-16 w-16 md:h-24 md:w-24 border-4 border-gold-500 transition-transform group-hover:scale-105 cursor-pointer"
                  onClick={() => top3[0]?.id && handleAvatarClick(top3[0].id)}
                >
                  {top3[0]?.avatar ? (
                    <img
                      src={top3[0].avatar}
                      alt={top3[0]?.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gold-300 flex items-center justify-center">
                      <span className="text-gold-700 font-bold text-xl">
                        {(top3[0]?.name || 'U').charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                </span>
                <div className="absolute -bottom-1 -right-1 flex items-center gap-1 rounded-full bg-gold-500 px-2.5 py-1 text-xs font-bold text-black">
                  <span style={{ color: '#dfae30' }}>🔥</span>
                  <span>{top3[0]?.streak}</span>
                </div>
              </div>
              <h3 className="text-xs md:text-sm font-bold text-center break-words px-1 text-gradient mb-1 leading-tight">
                {top3[0]?.name || 'N/A'}
              </h3>
              <div className="text-2xl md:text-3xl font-bold text-gradient">{top3[0]?.total_points || top3[0]?.points || 0}</div>
              <div className="text-[9px] md:text-[11px] uppercase tracking-wider text-gray-300 mb-2">Điểm</div>
              <div className="mt-2 w-full rounded-t-lg transition-transform group-hover:scale-105 h-24 md:h-28 bg-gold-400 flex items-center justify-center">
                <div className="text-3xl md:text-4xl font-bold text-gold-700/50">1</div>
              </div>
            </motion.div>

            {/* Rank 3 - Bronze */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col items-center cursor-pointer group w-1/3 max-w-[120px] md:max-w-[150px]"
            >
              <div className="mb-2 rounded-full p-2.5 bg-amber-600 transition-transform group-hover:scale-110 flex items-center justify-center">
                <span className="text-xl md:text-2xl block leading-none">🥉</span>
              </div>
              <div className="relative mb-3">
                <span 
                  className="relative flex shrink-0 overflow-hidden rounded-full h-12 w-12 md:h-18 md:w-18 border-4 border-amber-700 transition-transform group-hover:scale-105 cursor-pointer"
                  onClick={() => top3[2]?.id && handleAvatarClick(top3[2].id)}
                >
                  {top3[2]?.avatar ? (
                    <img
                      src={top3[2].avatar}
                      alt={top3[2]?.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-amber-300 flex items-center justify-center">
                      <span className="text-amber-700 font-bold text-base">
                        {(top3[2]?.name || 'U').charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                </span>
                <div className="absolute -bottom-1 -right-1 flex items-center gap-1 rounded-full bg-gold-500 px-2.5 py-1 text-xs font-bold text-black">
                  <span style={{ color: '#dfae30' }}>🔥</span>
                  <span>{top3[2]?.streak}</span>
                </div>
              </div>
              <h3 className="text-xs md:text-sm font-bold text-center break-words px-1 mb-1 text-white leading-tight">
                {top3[2]?.name || 'N/A'}
              </h3>
              <div className="text-2xl md:text-3xl font-bold text-white">{top3[2]?.total_points || top3[2]?.points || 0}</div>
              <div className="text-[8px] md:text-[9px] uppercase tracking-wider mb-2 text-gray-300">Điểm</div>
              <div className="mt-2 w-full rounded-t-lg transition-transform group-hover:scale-105 h-18 md:h-20 bg-amber-600 flex items-center justify-center">
                <div className="text-3xl md:text-4xl font-bold text-amber-800/50">3</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
        {/* Leaderboard Table */}
        <section className={`py-8 px-4 relative ${theme === 'dark' ? '' : 'bg-white/50'}`}>
        <div className="container mx-auto max-w-7xl">
          <div className={`border-2 rounded-lg overflow-hidden ${
            theme === 'dark' 
              ? 'bg-gradient-to-br from-gold-900/20 to-black/50 border-gold-500/30' 
              : 'bg-white border-gold-400/50'
          }`}>
            {/* Table Header */}
            <div className={`grid grid-cols-[1fr_40px_45px_40px_35px_35px_40px] md:grid-cols-[1fr_50px_55px_50px_45px_40px_50px] gap-0.5 md:gap-1 p-1.5 md:p-2 border-b-2 text-[9px] md:text-[10px] font-bold ${
              theme === 'dark' 
                ? 'border-gold-500/50 text-gold-400' 
                : 'bg-gold-100/50 border-gold-400/50 text-gold-600'
            }`}
            style={theme === 'dark' ? { backgroundColor: '#fdf9f0' } : {}}
            >
              <div className="text-left pl-1">CẦU THỦ</div>
              <div className="text-center">ĐIỂM</div>
              <div className="text-center">VÒNG</div>
              <div className="text-center">BUỔI</div>
              <div className="text-center">%</div>
              <div className="text-center">🔥</div>
              <div className="text-center">MAX</div>
            </div>

            {/* Table Rows */}
            <div className={theme === 'dark' ? 'divide-y divide-gold-500/20' : 'divide-y divide-gray-200'}>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold-400 mx-auto mb-4"></div>
                  <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Đang tải...</p>
                </div>
              ) : filteredPlayers.length === 0 ? (
                <div className="text-center py-8">
                  <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Chưa có dữ liệu xếp hạng</p>
                </div>
              ) : (
                paginatedPlayers.map((player, index) => {
                  const originalIndex = players.findIndex(p => p.id === player.id)
                  const displayRank = startIndex + index + 1
                  return (
                  <motion.div
                    key={player.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.02 }}
                    className={`transition ${
                      theme === 'dark' 
                        ? 'hover:bg-gold-900/10'
                        : `${originalIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-gold-100/50`
                    }`}
                    style={theme === 'dark' ? { 
                      backgroundColor: 
                        originalIndex === 0 ? '#fffef0' :  // Rank 1: vàng nhạt
                        originalIndex === 1 ? '#f5f5f5' :  // Rank 2: bạc/xám nhạt
                        originalIndex === 2 ? '#fef8f0' :  // Rank 3: đồng/amber nhạt
                        '#ffffff'  // Còn lại: trắng
                    } : {}}
                  >
                    <div className="grid grid-cols-[1fr_40px_45px_40px_35px_35px_40px] md:grid-cols-[1fr_50px_55px_50px_45px_40px_50px] gap-0.5 md:gap-1 p-1.5 md:p-2 items-center text-[9px] md:text-[10px]">
                      <div className="flex items-center gap-1 md:gap-2 min-w-0">
                        <div className="flex-shrink-0 w-5 h-5 md:w-7 md:h-7 flex items-center justify-center">
                          {getPlayerIcon(player.status || 'neutral', displayRank)}
                        </div>
                        <div 
                          className="flex-shrink-0 w-5 h-5 md:w-8 md:h-8 rounded-full overflow-hidden border-2 border-gold-400/50 cursor-pointer hover:border-gold-500 transition"
                          onClick={() => player.id && handleAvatarClick(player.id)}
                        >
                          {player.avatar ? (
                            <img
                              src={player.avatar}
                              alt={player.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gold-200 flex items-center justify-center">
                              <span className="text-gold-700 font-bold text-[8px] md:text-xs">
                                {(player.name || 'U').charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1 min-w-0">
                          <span className="font-semibold text-[9px] md:text-[11px] break-words" style={{ color: '#353b46' }}>{player.name || player.username || 'N/A'}</span>
                          {isNewUser(player.date_joined) && (
                            <span className="px-1 py-0.5 bg-blue-500 text-white text-[7px] md:text-[8px] font-bold rounded whitespace-nowrap">New</span>
                          )}
                        </div>
                        <div className="flex-shrink-0 hidden md:block">
                          {getStatusIcon(player.status || 'neutral')}
                        </div>
                      </div>
                      <div className={`text-center font-bold text-[9px] md:text-[10px] ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}>{player.total_points || player.points || 0}</div>
                      <div className="text-center text-[9px] md:text-[10px]">
                        {(() => {
                          const roundValue = player.round || '0/0'
                          const [currentDay, totalDays] = roundValue.split('/')
                          return (
                            <>
                              <span style={{ color: '#353534' }}>{currentDay}</span>
                              <span className="hidden md:inline" style={{ color: '#b1b5b9' }}>/{totalDays || '31'}</span>
                            </>
                          )
                        })()}
                      </div>
                      <div className="text-center text-[9px] md:text-[10px]" style={{ color: '#1d2222' }}>{player.session || 0}</div>
                      <div className="text-center text-black font-medium text-[9px] md:text-[10px]">{player.percentage || 0}%</div>
                      <div className="text-center flex items-center justify-center gap-0.5">
                        <span className="text-[10px] md:text-sm" style={{ color: '#dfae30' }}>🔥</span>
                        <span className="text-red-400 font-bold text-[9px] md:text-[10px]">{player.streak || 0}</span>
                      </div>
                      <div className="text-center text-[9px] md:text-[10px] font-bold" style={{ color: '#dfae30' }}>{player.max || 0}</div>
                    </div>
                  </motion.div>
                  )
                })
              )}
            </div>
          </div>

          {/* Pagination */}
          {filteredPlayers.length > itemsPerPage && (
            <div className={`mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              <div className="text-sm">
                Hiển thị {startIndex + 1} - {Math.min(endIndex, filteredPlayers.length)} trong tổng số {filteredPlayers.length} cầu thủ
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 rounded-lg font-semibold transition ${
                    currentPage === 1
                      ? theme === 'dark'
                        ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : theme === 'dark'
                        ? 'bg-gold-500/20 text-gold-400 border border-gold-500/30 hover:bg-gold-500/30'
                        : 'bg-gold-50 text-gold-600 border border-gold-200 hover:bg-gold-100'
                  }`}
                >
                  <i className="pi pi-chevron-left"></i>
                </button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum
                    if (totalPages <= 5) {
                      pageNum = i + 1
                    } else if (currentPage <= 3) {
                      pageNum = i + 1
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i
                    } else {
                      pageNum = currentPage - 2 + i
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`w-10 h-10 rounded-lg font-semibold transition ${
                          currentPage === pageNum
                            ? theme === 'dark'
                              ? 'bg-gold-gradient text-black'
                              : 'bg-gold-gradient text-black'
                            : theme === 'dark'
                              ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                              : 'bg-white text-gray-700 hover:bg-gold-50 border border-gray-300'
                        }`}
                      >
                        {pageNum}
                      </button>
                    )
                  })}
                </div>

                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className={`px-4 py-2 rounded-lg font-semibold transition ${
                    currentPage === totalPages
                      ? theme === 'dark'
                        ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : theme === 'dark'
                        ? 'bg-gold-500/20 text-gold-400 border border-gold-500/30 hover:bg-gold-500/30'
                        : 'bg-gold-50 text-gold-600 border border-gold-200 hover:bg-gold-100'
                  }`}
                >
                  <i className="pi pi-chevron-right"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

        {/* User Profile Modal */}
        {selectedUserId && (
          <UserProfileModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            userId={selectedUserId}
          />
        )}
      </div>
    </main>
  )
}
